/**
 * TranslationContext — runtime i18n for the Consumer Trust Wallet.
 *
 * Two layers of translation working together:
 *   1. `<T>English</T>` component + `useT()` hook — explicit, useful
 *      for things like aria-labels set programmatically or for early
 *      first-paint translation of the shell.
 *   2. Automatic DOM sweep (see `dom-i18n.js`) — attaches a
 *      MutationObserver to `document.body` and translates every
 *      visible text node + user-facing attribute on every page,
 *      including pages we haven't manually wrapped with `<T>`.
 *
 * Batching + caching
 *   • Unknown strings are queued into a Set, drained every 250 ms.
 *   • Backend response merged into the current language's state and
 *     persisted to `localStorage.altara.wallet.i18n.<lang>` so
 *     future visits paint instantly.
 *   • English is a no-op fast path with instant restoration of every
 *     translated node/attribute (WeakMap-backed).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAccessibility } from "@/contexts/AccessibilityContext";
import { DomTranslator } from "@/lib/dom-i18n";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const CACHE_KEY = (lang) => `altara.wallet.i18n.${lang}`;
const BATCH_DEBOUNCE_MS = 250;
const MAX_BATCH = 60;

const TranslationCtx = createContext(null);

function loadCache(lang) {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY(lang));
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function saveCache(lang, map) {
  try { window.localStorage.setItem(CACHE_KEY(lang), JSON.stringify(map)); } catch { /* ignore quota */ }
}

export function TranslationProvider({ children }) {
  const { prefs } = useAccessibility();
  const language = prefs.language || "en";

  const [translations, setTranslations] = useState(() => (language === "en" ? {} : loadCache(language)));
  // Keep a ref of the live translations dict so the DOM translator can
  // read the newest value without being torn down and rebuilt each render.
  const translationsRef = useRef(translations);
  const languageRef = useRef(language);

  const pendingRef = useRef(new Set());
  const timerRef = useRef(null);
  const inFlightRef = useRef(new Set());
  const domTranslatorRef = useRef(null);

  useEffect(() => { translationsRef.current = translations; }, [translations]);

  const flush = useCallback(async () => {
    const lang = languageRef.current;
    if (lang === "en") return;
    const batch = Array.from(pendingRef.current).slice(0, MAX_BATCH);
    if (batch.length === 0) return;
    // Keep leftover strings for the next batch
    for (const s of batch) pendingRef.current.delete(s);
    batch.forEach((s) => inFlightRef.current.add(s));
    try {
      const { data } = await axios.post(`${API}/wallet/translate/batch`, {
        texts: batch,
        target_language: lang,
      });
      if (languageRef.current !== lang) return;
      const incoming = data?.translations || {};
      setTranslations((prev) => {
        const next = { ...prev, ...incoming };
        saveCache(lang, next);
        return next;
      });
      // Immediately ask the DOM translator to re-sweep so newly
      // translated strings appear without waiting for the next React
      // paint.
      domTranslatorRef.current?.refresh();
    } catch {
      /* leave sources on-screen; they'll be re-requested next paint */
    } finally {
      batch.forEach((s) => inFlightRef.current.delete(s));
      // If more strings arrived while we were in-flight, drain them.
      if (pendingRef.current.size > 0) {
        if (timerRef.current) return;
        timerRef.current = setTimeout(() => { timerRef.current = null; flush(); }, BATCH_DEBOUNCE_MS);
      }
    }
  }, []);

  const enqueue = useCallback((sourcesArr) => {
    const lang = languageRef.current;
    if (lang === "en") return;
    let scheduled = false;
    for (const source of sourcesArr) {
      if (!source || typeof source !== "string") continue;
      if (translationsRef.current[source]) continue;
      if (inFlightRef.current.has(source)) continue;
      pendingRef.current.add(source);
      scheduled = true;
    }
    if (!scheduled) return;
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      flush();
    }, BATCH_DEBOUNCE_MS);
  }, [flush]);

  // Bootstrap the DOM translator once, targeting the whole document body.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const target = document.body;
    if (!target) return;
    const dt = new DomTranslator(target, {
      getLanguage: () => languageRef.current,
      getTranslations: () => translationsRef.current,
      enqueue: (strings) => enqueue(strings),
    });
    domTranslatorRef.current = dt;
    dt.start();
    return () => { dt.stop(); domTranslatorRef.current = null; };
  }, [enqueue]);

  // React to language changes: swap cache, restore/sweep the DOM.
  useEffect(() => {
    languageRef.current = language;
    if (language === "en") {
      setTranslations({});
      // Immediately restore any previously-translated nodes so the
      // user sees English at once.
      domTranslatorRef.current?.restoreEnglish();
    } else {
      const cached = loadCache(language);
      setTranslations(cached);
      // Nudge the translator to sweep with the newly-loaded cache.
      // Wait for the next tick so React commits the state first.
      setTimeout(() => domTranslatorRef.current?.refresh(), 0);
    }
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    pendingRef.current.clear();
    inFlightRef.current.clear();
  }, [language]);

  const t = useCallback((source) => {
    if (!source || typeof source !== "string") return source;
    if (languageRef.current === "en") return source;
    const hit = translationsRef.current[source];
    if (hit) return hit;
    enqueue([source]);
    return source;
  }, [enqueue]);

  const value = useMemo(() => ({
    language,
    t,
    _cacheSize: Object.keys(translations).length,
  }), [language, t, translations]);

  return <TranslationCtx.Provider value={value}>{children}</TranslationCtx.Provider>;
}

export function useT() {
  const ctx = useContext(TranslationCtx);
  if (!ctx) return { language: "en", t: (s) => s };
  return ctx;
}

export function T({ children }) {
  const { t } = useT();
  if (typeof children !== "string") return children;
  return t(children);
}
